import { PLAN_KEYS, type TPlanKey } from "@/lib/feature-flags";
import Input from "@components/ui/input";
import Slider from "@components/ui/slider";
import Switch from "@components/ui/switch";
import { cn } from "@lib/cn";
import { IconChevronRight, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import {
  addOrgId,
  removeOrgId,
  togglePlan,
  type TEditableFlag,
} from "../feature-flags-editor.utils";
import { OrgIdList } from "./org-id-list";

const PLAN_LABELS: Record<TPlanKey, string> = {
  trial: "Trial",
  starter: "Starter",
  pro: "Pro",
  team: "Team",
};

export const FlagRow = ({
  flag,
  onChange,
  onRemove,
}: {
  flag: TEditableFlag;
  onChange: (patch: Partial<TEditableFlag>) => void;
  onRemove: () => void;
}) => {
  const [showAdvanced, setShowAdvanced] = useState(flag.audiences.length > 0);

  return (
    <section className="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-blue/10 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <Input
          type="text"
          value={flag.name}
          placeholder="flag-name"
          containerClassName="tw-flex-1"
          className="tw-font-mono"
          size="sm"
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <Switch>
          <Switch.Label className="tw-mr-1">Enabled</Switch.Label>
          <Switch.Toggle
            checked={flag.enabled}
            onCheckedChange={(checked) => onChange({ enabled: checked })}
          />
        </Switch>
        <button
          type="button"
          aria-label="Delete flag"
          onClick={onRemove}
          className="tw-flex tw-items-center tw-text-blue-500 hover:tw-text-red-600"
        >
          <IconTrash className="tw-size-4" />
        </button>
      </div>

      <Input
        type="text"
        value={flag.description ?? ""}
        placeholder="Description (private - e.g. Collaboration beta)"
        size="sm"
        onChange={(e) => onChange({ description: e.target.value })}
      />

      <OrgIdList
        label="Allowlist org IDs"
        ids={flag.allowlistOrgIds}
        onAdd={(value) =>
          onChange({ allowlistOrgIds: addOrgId(flag.allowlistOrgIds, value) })
        }
        onRemove={(index) =>
          onChange({
            allowlistOrgIds: removeOrgId(flag.allowlistOrgIds, index),
          })
        }
      />
      <OrgIdList
        label="Blocklist org IDs"
        ids={flag.blocklistOrgIds}
        onAdd={(value) =>
          onChange({ blocklistOrgIds: addOrgId(flag.blocklistOrgIds, value) })
        }
        onRemove={(index) =>
          onChange({
            blocklistOrgIds: removeOrgId(flag.blocklistOrgIds, index),
          })
        }
      />

      <div className="tw-flex tw-flex-col tw-gap-2">
        <span className="tw-font-sans tw-text-xs tw-text-blue-500">
          Rollout - {flag.rolloutPercentage}%
        </span>
        <Slider
          min={0}
          max={100}
          step={1}
          value={[flag.rolloutPercentage]}
          onValueChange={(value) => onChange({ rolloutPercentage: value[0] })}
        />
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2">
        <button
          type="button"
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced((prev) => !prev)}
          className="tw-flex tw-items-center tw-gap-1 tw-self-start tw-font-sans tw-text-xs tw-font-medium tw-text-blue-500 hover:tw-text-blue"
        >
          <IconChevronRight
            className={cn(
              "tw-size-3.5 tw-transition-transform",
              showAdvanced && "tw-rotate-90",
            )}
          />
          Advanced settings
        </button>

        {showAdvanced && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-pl-4">
            <span className="tw-font-sans tw-text-xs tw-text-blue-500">
              Plans - none selected = all plans
            </span>
            <div className="tw-flex tw-gap-2">
              {PLAN_KEYS.map((plan) => {
                const selected = flag.audiences.includes(plan);
                return (
                  <button
                    key={plan}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      onChange({ audiences: togglePlan(flag.audiences, plan) })
                    }
                    className={cn(
                      "tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-1 tw-font-sans tw-text-xs tw-font-medium tw-transition-colors",
                      selected
                        ? "tw-border-blue tw-bg-blue tw-text-white"
                        : "tw-border-blue/20 tw-bg-white tw-text-blue-500 hover:tw-border-blue/40",
                    )}
                  >
                    {PLAN_LABELS[plan]}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {flag.createdAt && (
        <span className="tw-font-sans tw-text-[11px] tw-text-blue-400">
          Created {new Date(flag.createdAt).toLocaleDateString()}
        </span>
      )}
    </section>
  );
};
